import { useState } from "react";
import { Link } from "react-router-dom";

type TBrandSummary = {
  brand: string;
  total_videos: number;
  brand_videos: {
    ads: number;
    reels: number;
    total: number;
  };
  competitor_videos: {
    ads: number;
    reels: number;
    total: number;
  };
  competitors: number;
};

const BrandCartSkeleton = () => (
  <div className="w-full animate-pulse">
    <div className="h-6 bg-gray-200 rounded w-48 mb-6"></div>
    <div className="bg-white border border-gray-100 rounded-xl p-8 shadow-sm">
      {/* Brand Info Skeleton */}
      <div className="flex items-center space-x-4 mb-8">
        <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
        <div className="h-7 bg-gray-200 rounded w-40"></div>
      </div>

      {/* Metrics Skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        {[1, 2, 3].map((i) => (
          <div key={i} className="text-center space-y-3">
            <div className="h-8 bg-gray-200 rounded w-16 mx-auto"></div>
            <div className="h-4 bg-gray-200 rounded w-24 mx-auto"></div>
            <div className="h-3 bg-gray-200 rounded w-32 mx-auto"></div>
          </div>
        ))}
      </div>

      {/* Actions Skeleton */}
      <div className="flex justify-end space-x-3">
        <div className="h-10 bg-gray-200 rounded-lg w-28"></div>
        <div className="h-10 bg-gray-200 rounded-lg w-32"></div>
      </div>
    </div>
  </div>
);

const BrandCartTable = ({
  isLoading,
  brandData,
}: {
  isLoading: boolean;
  brandData: TBrandSummary;
}) => {
  const [activeAction, setActiveAction] = useState<string | null>(null);

  if (isLoading) {
    return <BrandCartSkeleton />;
  }

  if (!brandData) {
    return (
      <div className="w-full">
        <div className="bg-white border border-gray-100 rounded-xl p-12 text-center shadow-sm">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Brand Selected
          </h3>
          <p className="text-gray-500 text-sm">
            Select a brand to view its summary and analytics
          </p>
        </div>
      </div>
    );
  }

  const getBrandInitials = (brand: string) => {
    return (
      brand
        ?.split(" ")
        .map((word) => word.charAt(0))
        .join("")
        .slice(0, 2)
        .toUpperCase() || "B"
    );
  };

  const getGradientByBrand = (brand: string) => {
    const gradients = [
      "bg-gradient-to-br from-blue-500 to-blue-600",
      "bg-gradient-to-br from-purple-500 to-purple-600",
      "bg-gradient-to-br from-green-500 to-green-600",
      "bg-gradient-to-br from-orange-500 to-orange-600",
      "bg-gradient-to-br from-pink-500 to-pink-600",
      "bg-gradient-to-br from-indigo-500 to-indigo-600",
    ];
    const index = brand?.length ? brand.charCodeAt(0) % gradients.length : 0;
    return gradients[index];
  };

  const handleActionClick = (action: string) => {
    setActiveAction(action);
    // Simulate navigation
    setTimeout(() => setActiveAction(null), 1500);
  };

  return (
    <div className="w-full">
      <div className="bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200">
        {/* Header Section */}
        <div className="p-8 border-b border-gray-50">
          <div className="flex items-center space-x-6">
            <div
              className={`w-16 h-16 ${getGradientByBrand(
                brandData?.brand
              )} flex items-center justify-center rounded-xl shadow-lg`}
            >
              <span className="text-xl font-bold text-white">
                {getBrandInitials(brandData?.brand)}
              </span>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-1">
                {brandData?.brand}
              </h3>
              <p className="text-sm text-gray-500">
                Brand Performance Dashboard
              </p>
            </div>
          </div>
        </div>

        {/* Metrics Section */}
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* Brand Videos */}
            <div className="group">
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-6 text-center hover:bg-gray-100 hover:border-gray-200 transition-all duration-200">
                <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-2">
                  {brandData?.brand_videos?.total || 0}
                </p>
                <p className="text-base font-semibold text-gray-700 mb-3">
                  Brand Videos
                </p>
                <div className="flex justify-center space-x-4 text-sm text-gray-600">
                  <span className="bg-white border border-gray-200 px-3 py-1 rounded-full">
                    Ads: {brandData?.brand_videos?.ads || 0}
                  </span>
                  <span className="bg-white border border-gray-200 px-3 py-1 rounded-full">
                    Reels: {brandData?.brand_videos?.reels || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Competitor Videos */}
            <div className="group">
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-6 text-center hover:bg-gray-100 hover:border-gray-200 transition-all duration-200">
                <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-2">
                  {brandData?.competitor_videos?.total || 0}
                </p>
                <p className="text-base font-semibold text-gray-700 mb-3">
                  Competitor Videos
                </p>
                <div className="flex justify-center space-x-4 text-sm text-gray-600">
                  <span className="bg-white border border-gray-200 px-3 py-1 rounded-full">
                    Ads: {brandData?.competitor_videos?.ads || 0}
                  </span>
                  <span className="bg-white border border-gray-200 px-3 py-1 rounded-full">
                    Reels: {brandData?.competitor_videos?.reels || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Competitors Count */}
            {/* <div className="group">
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-6 text-center hover:bg-gray-100 hover:border-gray-200 transition-all duration-200">
                <div className="w-12 h-12 bg-gray-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-2">
                  {brandData?.competitors || 0}
                </p>
                <p className="text-base font-semibold text-gray-700 mb-3">
                  Competitors
                </p>
                <div className="text-sm text-gray-600">
                  <span className="bg-white border border-gray-200 px-3 py-1 rounded-full">
                    Active Tracking
                  </span>
                </div>
              </div>
            </div> */}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4">
            <Link
              to={"/products"}
              className="inline-flex items-center justify-center px-6 py-3 text-sm font-medium text-[#ec5e01] bg-orange-50 hover:bg-orange-100 hover:text-[#d15503] border border-orange-200 rounded-lg transition-all duration-200 hover:shadow-sm disabled:opacity-50"
            >
              <>
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
                View Products
              </>
            </Link>
            <Link
              to={`/brand-cart-details/${brandData.brand}`}
              className="inline-flex items-center justify-center px-6 py-3 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all duration-200 hover:shadow-lg transform hover:-translate-y-0.5 disabled:opacity-50 disabled:transform-none"
            >
              <>
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2-2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 00-2-2z"
                  />
                </svg>
                View Details
              </>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandCartTable;
